import { MouseEventHandler, useCallback, useContext, useEffect, useState } from "react";
import useBasketActions from "../../../Utils/Hooks/useBasketActions";
import useTrashActions from "../../../Utils/Hooks/useTrash";
import useCollectionActions from "../../../Utils/Hooks/useCollectionActions";
import { useSelector } from "react-redux";
import { GetExports } from "../../../Redux/Reducers/redux-slice-asset";
import { ModalContext } from "../../Modals";
import ModalCreateShare from "../../Modals/ModalCreateShare/modal-create-share";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router";
import { routePaths } from "../../../Router/router";
import ModalRenameObject, { ModalRenameObjectElementProps } from "../../Modals/ModalRenameObject/modal-rename-object";
import { ObjectType } from "../../../Core/Api/Types/object-type";
import { assetService, cacheService, dataService } from "../../../Core/Api/services";
import { AclMatch, UserHasAcls } from "../../../Utils/helpers";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import { Box, IconButton, ListItemIcon, ListSubheader, Menu, MenuItem, Typography } from "@mui/material";
import {
  IsPermissionGranted,
  IsAssetDownloadable,
  IsAssetOrderable,
  IsObjectShareable,
  IsClipDownloadable,
} from "../../../Core/Utils/asset-object-utils";
import { PermissionType } from "../../../Core/Api/Types/permission-type";
import {
  BookmarkBorderOutlined,
  DeleteOutline,
  EditOutlined,
  ExpandMoreOutlined,
  FileDownloadOutlined,
  FileOpenOutlined,
  ImageOutlined,
  Inventory2Outlined,
  MoreHoriz,
  MoreVert,
  RestoreFromTrashOutlined,
  SaveAltOutlined,
  ShareOutlined,
  ShoppingBagOutlined,
} from "@mui/icons-material";
import { AclTypes } from "../../../Core/Api/Types/acl-types";
import { DbAssetObjectInfos } from "../../../Core/Api/Types/db-asset-object-infos";
import ModalEditMetadata, { ModalEditMetadataElementProps } from "../../Modals/ModalEditMetadata/modal-edit-metadata";
import { DbDeliveryMetadataExportInfos } from "../../../Core/Api/Types/db-delivery-metadata-export-infos";
import ModalUploadMediaFiles, {
  ModalUploadMediaFilesElementProps,
} from "../../Modals/ModalUploadMediaFiles/modal-upload-media-files";
import { useTranslation } from "react-i18next";
import { DownloadMenuItem } from "./download-menu-item";
import { RenameObj, TrashObj, UntrashObj } from "../../../Redux/Reducers/redux-slice-drive";
import { RemoveChildrenFromTreeObj, RenameChildInTreeObj, TreeContext } from "../../../Redux/Reducers/redux-slice-tree";
import { ModalDownloadClip, ModalDownloadClipElementProps } from "../../Modals/ModalDownloadClip/modal-download-clip";
import { FileLinkedType } from "../../../Core/Api/Types/file-linked-type";
import { DownloadExportAssetType } from "../../../Core/Api/Data/data-api-service";
import ModalLoader, { ModalLoaderElementProps } from "../../Modals/ModalLoader/modal-loader";

interface DriveActionProps {
  context?: "card" | "breadcrumb" | "table";
  object?: DbAssetObjectInfos;
  objId: number;
  openPanel?: () => void;
}

export function DriveActions(props: DriveActionProps) {
  const { t } = useTranslation();
  // Folder / asset actions
  const { object, objId, context, openPanel } = props;
  const [obj, setObj] = useState<DbAssetObjectInfos | null>(object ?? null);
  const [exports, setExports] = useState<DbDeliveryMetadataExportInfos[] | null>(useSelector(GetExports));

  // Hooks
  const modalContext = useContext(ModalContext);
  const { openModal, closeModal } = { ...modalContext };
  const { addObjectByIdsToBasket } = useBasketActions();
  const { TrashObjectByIds, UntrashObjectByIds } = useTrashActions();
  const collectionActions = useCollectionActions();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const enqueueCustomSnackbar = useCustomSnackbar();
  const user = cacheService.GetUserInfo();

  const [anchorMenuEl, setAnchorMenuEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorMenuEl);
  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorMenuEl(event.currentTarget);
  };
  const handleMoreClose = () => {
    setAnchorMenuEl(null);
  };
  const successMessage = t("alert:success.dataComputingInBackground");

  // Handlers
  useEffect(() => {
    if (
      UserHasAcls([AclTypes.aclSuperUser, AclTypes.aclMetadataExport], AclMatch.OneAtLeast) &&
      exports === null &&
      menuOpen
    )
      dataService.GetAssetsMetadataExports().then((response) => setExports(response.exports || []));
  }, [menuOpen]);

  useEffect(() => {
    if ((obj === null || obj?.id !== objId) && objId)
      assetService.GetObjectLightById(objId).then((response) => {
        if (response.graph) setObj(response.graph);
      });
  }, [objId]);

  const handleShare: React.MouseEventHandler = useCallback(
    (e) => {
      e.stopPropagation();

      if (!openModal) return;

      openModal(ModalCreateShare, { object: obj });
      handleMoreClose();
    },
    [obj]
  );

  const handleEditMetadata: React.MouseEventHandler = useCallback(
    (e) => {
      e.stopPropagation();

      if (!openModal) return;

      if (!obj) {
        assetService.GetObjectById(objId).then((res) => {
          if (res.success && res.graph) {
            openModal<ModalEditMetadataElementProps>(ModalEditMetadata, { objects: [res.graph] });
          } else enqueueCustomSnackbar(res.message || t("alert:error.assetNotFound"), "error");
        });
      } else openModal<ModalEditMetadataElementProps>(ModalEditMetadata, { objects: [obj] });

      handleMoreClose();
    },
    [obj]
  );

  const handleOrder: React.MouseEventHandler = useCallback(
    (e) => {
      e.stopPropagation();
      addObjectByIdsToBasket([objId]);
      handleMoreClose();
    },
    [obj]
  );

  const handleOrderClip: MouseEventHandler = useCallback(
    (e) => {
      e.stopPropagation();
      if (!openModal) return;

      setAnchorMenuEl(null);

      openModal<ModalDownloadClipElementProps>(ModalDownloadClip, {
        clipId: objId,
      });
    },
    [obj]
  );

  const handleTrash: React.MouseEventHandler = useCallback(
    (e) => {
      e.stopPropagation();
      TrashObjectByIds([objId], (id) => {
        dispatch(TrashObj({ objId: id }));
        dispatch(RemoveChildrenFromTreeObj({ children: [id], context: TreeContext.DriveTreeview }));
      });
      handleMoreClose();
    },
    [obj]
  );

  const handleUntrash: React.MouseEventHandler = useCallback(
    (e) => {
      e.stopPropagation();
      UntrashObjectByIds([objId], (id) => {
        dispatch(UntrashObj({ objId: id }));
      });
      handleMoreClose();
    },
    [obj]
  );

  const handleGoToAsset: React.MouseEventHandler = useCallback(
    (e) => {
      e.stopPropagation();
      navigate(routePaths.asset.path + "/" + objId);
      handleMoreClose();
    },
    [objId]
  );

  const handleRename: React.MouseEventHandler = useCallback(
    (e) => {
      e.stopPropagation();

      if (!openModal) return;

      openModal<ModalRenameObjectElementProps>(ModalRenameObject, {
        nameInitial: obj!.name,
        objectId: objId,
        onConfirm: (name: string) => {
          dispatch(RenameObj({ objId: objId, newName: name }));
          dispatch(RenameChildInTreeObj({ objId: objId, newName: name, context: TreeContext.DriveTreeview }));
        },
      });
      handleMoreClose();
    },
    [obj]
  );

  const handleAddToCollectionModal: React.MouseEventHandler = useCallback(
    (e) => {
      e.stopPropagation();
      collectionActions.AddItemsToCollection([obj!]);
      handleMoreClose();
    },
    [obj]
  );

  const handleClickUploadMedia = () => {
    if (!openModal) return;
    openModal<ModalUploadMediaFilesElementProps>(ModalUploadMediaFiles, {
      objectId: Number(obj!.id),
      objectType: obj?.type ?? ObjectType.Unknown,
      defaultLinkedType: FileLinkedType.PosterTitleMain,
      context: "drive",
      onSuccess: () => {
        // force image refresh
        cacheService.SetLastUpdatedMediaTimestamp(new Date().getTime());
      },
    });
  };

  // xml export
  const handleExportAsXml = useCallback(
    (e) => {
      enqueueCustomSnackbar(t("alert:primary.generatingExport"), "primary");
      assetService.ExportAssetAsXml(obj!.id, true).then((response) => {
        enqueueCustomSnackbar(response.message, response.success ? "success" : "error");
      });
    },
    [obj?.id]
  );

  // export sessions
  const handleExportBySession = (templateId: number) => {
    handleMoreClose();
    openModal &&
      openModal<ModalLoaderElementProps>(ModalLoader, {
        title: t("alert:primary.downloadStarted"),
        body: t("alert:primary.downloadStartedButMightTakeAWhile"),
        warning: t("alert:warning.doNotCloseModalWhileDownloadProcessing"),
      });

    dataService
      .DownloadExportAssetByTemplate(templateId, obj!.id)
      .then((response) => {
        closeModal && closeModal();
        if (response === DownloadExportAssetType.EmailLink) enqueueCustomSnackbar(successMessage, "success");
      })
      .catch(() => {
        closeModal && closeModal();
        enqueueCustomSnackbar(t("alert:error.downloadFailed"), "error");
      });
  };

  // Rendering
  if (obj === null) return <></>;

  const canUploadThumbnail =
    !user?.isExternalAccessSpecific && [ObjectType.Folder, ObjectType.Group].includes(obj?.type);
  const canAddToCollection = !user?.isExternalAccessSpecific;
  const canDownload = IsAssetDownloadable(obj);
  const canDownloadClip = IsClipDownloadable(obj);
  const canShare = IsPermissionGranted(obj, PermissionType.Share) && IsObjectShareable(obj);
  const canOrder = IsAssetOrderable(obj);
  const canRename = IsPermissionGranted(obj, PermissionType.MetadataEdit);
  const canOpenAssetPage = obj.type !== ObjectType.Folder && !user?.isExternalAccessSpecific;

  const canEditMetadata =
    IsPermissionGranted(obj, PermissionType.MetadataEdit) &&
    ![ObjectType.Folder, ObjectType.Generic].includes(obj!.type);

  const canTrash = IsPermissionGranted(obj, PermissionType.Trash);

  return (
    <Box
      sx={(theme) => ({
        ...(context === "card" && {
          background: theme.palette.background.paper,
          border: `1px solid ${theme.palette.backgroundAlpha.border}`,
          boxShadow: theme.shadows[2],
        }),
        borderRadius: 1,
      })}
    >
      <IconButton size="small" onClick={handleMenuOpen} color="ghost">
        {context === "card" ? <MoreHoriz /> : context === "table" ? <MoreVert /> : <ExpandMoreOutlined />}
      </IconButton>
      <Menu anchorEl={anchorMenuEl} open={menuOpen} onClose={handleMoreClose} sx={{ overflow: "visible" }}>
        <ListSubheader sx={{ py: 1, px: 2 }}>
          <Typography variant="body1">{t("common:actions")}</Typography>
        </ListSubheader>

        {canRename && (
          <MenuItem onClick={handleRename}>
            <ListItemIcon>
              <EditOutlined fontSize="small" />
            </ListItemIcon>
            {t("common:rename")}
          </MenuItem>
        )}
        <DownloadMenuItem canDownload={canDownload} obj={obj} handleMoreClose={handleMoreClose} />
        {canDownloadClip && (
          <MenuItem onClick={handleOrderClip}>
            <ListItemIcon>
              <FileDownloadOutlined fontSize="small" />
            </ListItemIcon>
            {t("button:orderDownload")}
          </MenuItem>
        )}
        {canEditMetadata && (
          <MenuItem onClick={handleEditMetadata}>
            <ListItemIcon>
              <Inventory2Outlined fontSize="small" />
            </ListItemIcon>
            {t("common:editMetadata")}
          </MenuItem>
        )}
        {canShare && (
          <MenuItem onClick={handleShare}>
            <ListItemIcon>
              <ShareOutlined fontSize="small" />
            </ListItemIcon>
            {t("common:share")}
          </MenuItem>
        )}
        {canUploadThumbnail && (
          <MenuItem onClick={handleClickUploadMedia}>
            <ListItemIcon>
              <ImageOutlined fontSize="small" />
            </ListItemIcon>
            {t("menu:item.uploadThumbnail")}
          </MenuItem>
        )}
        {canAddToCollection && (
          <MenuItem onClick={handleAddToCollectionModal}>
            <ListItemIcon>
              <BookmarkBorderOutlined fontSize="small" />
            </ListItemIcon>
            {t("button:addToCollection_one")}
          </MenuItem>
        )}
        {canOrder && (
          <MenuItem onClick={handleOrder}>
            <ListItemIcon>
              <ShoppingBagOutlined fontSize="small" />
            </ListItemIcon>
            {t("common:order")}
          </MenuItem>
        )}
        {canOpenAssetPage && (
          <MenuItem onClick={handleGoToAsset}>
            <ListItemIcon>
              <FileOpenOutlined fontSize="small" />
            </ListItemIcon>
            {t("menu:item.openAssetPage")}
          </MenuItem>
        )}
        {canTrash && (
          <MenuItem onClick={handleTrash}>
            <ListItemIcon>
              <DeleteOutline fontSize="small" />
            </ListItemIcon>
            {t("common:trash")}
          </MenuItem>
        )}
        {obj.trash && (
          <MenuItem onClick={handleUntrash}>
            <ListItemIcon>
              <RestoreFromTrashOutlined fontSize="small" />
            </ListItemIcon>
            {t("common:untrash")}
          </MenuItem>
        )}
        <ListSubheader sx={{ py: 1, px: 2 }}>
          <Typography variant="body1">{t("component:body.exportMetadata")}</Typography>
        </ListSubheader>
        <MenuItem onClick={handleExportAsXml}>
          <ListItemIcon>
            <SaveAltOutlined fontSize="small" />
          </ListItemIcon>
          {t("menu:item.exportAsXml")}
        </MenuItem>
        {/* <MenuItem onClick={openModalDownloadPdf}>Export as PDF</MenuItem> */}
        {exports &&
          exports.map((_export) => (
            <MenuItem key={_export.id} onClick={() => handleExportBySession(_export.id)}>
              <ListItemIcon>
                <SaveAltOutlined fontSize="small" />
              </ListItemIcon>
              {_export.name}
            </MenuItem>
          ))}
      </Menu>
    </Box>
  );
}
